package link.erpango.quip.domain.entities

import kotlinx.serialization.EncodeDefault
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * Repuesto personal del usuario ("mis repuestos").
 * Entidad independiente del catálogo de mantenimientos (DSparePart).
 *
 * Los campos `id`, `createdAt` y `updatedAt` son solo lectura: los devuelve el backend.
 * En peticiones de creación/actualización, `compatibleVehicles` y `vehicleVins` tienen
 * semántica replace-all cuando están presentes; si no se envían (PATCH parcial) no se tocan.
 */
@Serializable
data class DMySparePart(
    val id: Long = 0L, // Backend ID (solo lectura en respuestas)
    val categoryId: String,
    val sparePartId: String,
    val brand: String,
    val model: String,
    val additionalInfo: String? = null,
    val durabilityDays: Int? = null,
    val reference: String? = null,
    @SerialName("created_at")
    val createdAt: String? = null,
    @SerialName("updated_at")
    val updatedAt: String? = null,
    // @EncodeDefault para que una lista vacía se envíe siempre (vacía = limpiar en el backend)
    @EncodeDefault
    @SerialName("compatible_vehicles")
    val compatibleVehicles: List<DCompatibleVehicle> = emptyList(),
    @EncodeDefault
    @SerialName("vehicle_vins")
    val vehicleVins: List<String> = emptyList()
)

@Serializable
data class DCompatibleVehicle(
    @SerialName("brand_id")
    val brandId: Int? = null,
    @SerialName("model_id")
    val modelId: Int? = null
)

/**
 * Resultado tipado del endpoint de compartir un repuesto.
 * Tanto el 201 (compartido) como el 409 (ya existe copia para ese usuario)
 * se modelan como resultados, no como excepciones.
 */
sealed class DShareResult {

    /** 201: se ha creado la copia del repuesto para el usuario destino. */
    data class Shared(
        val sharedSparePartId: Long
    ) : DShareResult()

    /** 409: ya existía una copia para ese usuario; devuelve su ID. */
    data class AlreadyShared(
        val existingCopyId: Long,
        val message: String? = null
    ) : DShareResult()
}

@Serializable
data class DShareSparePartRequest(
    @SerialName("target_user_id")
    val targetUserId: Int
)

/**
 * Payload de respuesta del endpoint de compartir (201 y 409 comparten forma).
 */
@Serializable
data class DShareSparePartResponse(
    val message: String? = null,
    val error: String? = null,
    @SerialName("shared_spare_part_id")
    val sharedSparePartId: Long? = null
)
